import styled from 'styled-components';

const BORDER = '#2e2e32';
const SURFACE = '#18181b';
const MUTED = '#a1a1aa';
const TEXT = '#fafafa';

export const Container = styled.div<{ $withBar?: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: calc(100% - 32px);
	max-width: 560px;
	margin: 16px auto;
	padding-bottom: ${({ $withBar }) => ($withBar ? '200px' : '0')};
	color: ${TEXT};
	box-sizing: border-box;
`;

export const SearchInput = styled.input`
	width: 100%;
	height: 52px;
	padding: 0 14px;
	border: 1px solid ${BORDER};
	border-radius: 8px;
	background: ${SURFACE};
	color: ${TEXT};
	/* 16px keeps iOS Safari from zooming into the field */
	font-size: 16px;
	box-sizing: border-box;
	outline: none;

	&::placeholder {
		color: #71717a;
	}

	&:focus {
		border-color: ${MUTED};
	}
`;

export const Hint = styled.p<{ $error?: boolean }>`
	margin: 0;
	padding: 4px 2px;
	font-size: 14px;
	color: ${({ $error }) => ($error ? '#f87171' : MUTED)};
`;

export const Results = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	border: 1px solid ${BORDER};
	border-radius: 8px;
	background: ${SURFACE};
	overflow: hidden;
`;

export const ResultButton = styled.button`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	width: 100%;
	min-height: 60px;
	padding: 10px 14px;
	border: none;
	border-bottom: 1px solid ${BORDER};
	background: transparent;
	color: ${TEXT};
	text-align: left;
	cursor: pointer;

	li:last-child > & {
		border-bottom: none;
	}

	&:active {
		background: #232327;
	}
`;

export const Name = styled.span`
	font-size: 16px;
	font-weight: 600;
	word-break: break-word;
`;

export const Email = styled.span`
	font-size: 14px;
	color: ${MUTED};
	word-break: break-all;
`;

export const Person = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	margin-left: -8px;
`;

export const PersonText = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1;
	min-width: 0;
`;

export const BackButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 44px;
	height: 44px;
	padding: 0;
	border: none;
	background: transparent;
	color: ${TEXT};
	cursor: pointer;

	&:active {
		opacity: 0.6;
	}
`;

export const ListHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	min-height: 32px;
	font-size: 13px;
	color: ${MUTED};
	text-transform: uppercase;
	letter-spacing: 0.04em;
`;

export const TextButton = styled.button`
	min-height: 40px;
	padding: 0 4px;
	border: none;
	background: transparent;
	color: ${TEXT};
	font-size: 15px;
	font-weight: 600;
	text-transform: none;
	letter-spacing: 0;
	cursor: pointer;

	&:disabled {
		opacity: 0.5;
	}
`;

export const TicketList = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	border: 1px solid ${BORDER};
	border-radius: 8px;
	background: ${SURFACE};
	overflow: hidden;
`;

export const TicketRow = styled.button<{ $color: string; $selected: boolean }>`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	min-height: 68px;
	padding: 12px 16px 12px 14px;
	border: none;
	border-left: 10px solid ${({ $color }) => $color};
	border-bottom: 1px solid ${BORDER};
	background: ${({ $selected }) => ($selected ? '#232327' : 'transparent')};
	color: ${TEXT};
	text-align: left;
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;

	li:last-child > & {
		border-bottom: none;
	}

	&[aria-disabled='true'] {
		cursor: default;
	}
`;

export const TicketText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1;
	min-width: 0;
`;

export const TicketName = styled.span`
	font-size: 16px;
	font-weight: 600;
	word-break: break-word;
`;

export const TicketSub = styled.span`
	font-size: 14px;
	color: ${TEXT};
`;

export const TicketStatus = styled.span<{ $tone: 'checking' | 'ready' | 'in' | 'blocked' }>`
	font-size: 13px;
	color: ${({ $tone }) => ($tone === 'in' ? '#4ade80' : $tone === 'blocked' ? '#f87171' : MUTED)};
`;

export const TicketId = styled.span`
	color: #71717a;
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 12px;
`;

export const CheckedIn = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 26px;
	height: 26px;
	border-radius: 50%;
	background: #16a34a;
	color: #ffffff;
`;

export const Box = styled.span<{ $checked: boolean; $undo: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 26px;
	height: 26px;
	border-radius: 6px;
	border: 2px solid ${({ $checked, $undo }) => ($checked ? ($undo ? '#f87171' : TEXT) : '#52525b')};
	background: ${({ $checked, $undo }) => ($checked ? ($undo ? '#f87171' : TEXT) : 'transparent')};
	color: #09090b;
`;

export const ActionBar = styled.div`
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 5;
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
	border-top: 1px solid ${BORDER};
	background: ${SURFACE};

	& > * {
		width: 100%;
		max-width: 560px;
		margin: 0 auto;
	}
`;

export const InlineError = styled.span`
	margin-top: 4px;
	font-size: 13px;
	color: #f87171;
`;
